
import React, { useState } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { useHistory } from 'react-router-dom';
import useStyles from '../../../assets/styles/DQProV2LoginStyles';
import PoweredByDQ_Default from '../../../assets/images/PoweredByDQ_Default.png';
import PoweredByDQ_White from '../../../assets/images/PoweredByDQ_white.png';
import { useColorMode } from '../../../context/colorMode/colorMode.context';
import { initResetPassword, resetPassword } from '../../../services/auth.service';
import LoaderWithBackDrop from '../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp';
import PasswordWithValidation, { getPasswordChecks } from './components/PasswordWithValidationsField.comp';

const DQProV2ResetPassword = () => {
    const classes = useStyles();
    const { isDark } = useColorMode();
    const PoweredByDQ_Logo = isDark ? PoweredByDQ_White : PoweredByDQ_Default;
    const history = useHistory();

    // Stages: 'request' (email), 'sent' (success msg), 'reset' (enter code and new pass)
    const [stage, setStage] = useState('request'); 
    const [email, setEmail] = useState('');
    const [resetCode, setResetCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({ password: '', confirmPassword: '' });
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleSendLink = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;

        try {
            setLoading(true);
            await initResetPassword(email.trim());
            setStage('sent');
        } catch (error) {
            // error already toasted by invokeApi
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!resetCode.trim() || !newPassword.trim()) return;

        const newErrors = { password: '', confirmPassword: '' };
        const checks = getPasswordChecks(newPassword);
        if (!Object.values(checks).every(Boolean)) {
            newErrors.password = 'Password must include 8+ chars, upper, lower, number, and special character.';
        }
        if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = "The Confirm Password confirmation doesnt match.";
        }
        setErrors(newErrors);
        if (newErrors.password || newErrors.confirmPassword) return;

        try {
            setLoading(true);
            await resetPassword(resetCode.trim(), newPassword);
            history.push('/login', { resetSuccess: true });
        } catch (error) {
            // error already toasted by invokeApi
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box className={classes.loginPage}>
            <Box className={classes.container}>
                <Typography className={classes.welcomeText}>
                    {stage === 'reset' ? 'Set New Password' : stage === 'sent' ? 'Check your Inbox' : 'Reset Password'}
                </Typography>
                <img 
                    src={PoweredByDQ_Logo} 
                    alt="Powered by DQ" 
                    className={classes.logo} 
                    style={{ cursor: 'pointer', marginBottom: 0 }}
                    onClick={() => history.push('/my_workspace')}
                />

                {stage === 'request' && (
                    <Box sx={{ width: '100%', mt: 2 }}>
                        <Typography sx={{ 
                            fontSize: '16px', 
                            color: 'var(--brand-text-muted, #666)',
                            mb: 4,
                            textAlign: 'center',
                            fontFamily: "'Roboto', sans-serif" 
                        }}>
                            Enter your email address and we'll send you a link to reset your password.
                        </Typography>

                        <form className={classes.form} onSubmit={handleSendLink}>
                            <Typography className={classes.label}>Email</Typography>
                            <input
                                className={classes.input}
                                type="email"
                                placeholder="Enter your email address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={loading}
                                required
                            />
                            
                            <Button
                                className={classes.continueButton}
                                type="submit"
                                disabled={loading || !email.trim()}
                                sx={{ mt: 3 }}
                            >
                                {loading ? 'Sending...' : 'Send Reset Link'}
                            </Button>
                        </form>
                    </Box>
                )}

                {stage === 'sent' && (
                    <Box sx={{ width: '100%', mt: 2, textAlign: 'center' }}>
                        <Typography sx={{ 
                            fontSize: '16px', 
                            color: 'var(--brand-text-muted, #666)',
                            mb: 4,
                            fontFamily: "'Roboto', sans-serif" 
                        }}>
                            We've sent a password reset link to <strong>{email}</strong>. Please check your email and follow the instructions.
                        </Typography>
                        
                        <Button
                            className={classes.continueButton}
                            onClick={() => setStage('reset')}
                        >
                            Enter Reset Code
                        </Button>

                        <Button
                            variant="text"
                            onClick={() => setStage('request')}
                            sx={{ mt: 2, color: 'var(--brand-text-muted, #666)', textTransform: 'none', fontWeight: 'bold' }}
                        >
                            Back to Request
                        </Button>
                    </Box>
                )}

                {stage === 'reset' && (
                    <Box sx={{ width: '100%', mt: 2 }}>
                        <form className={classes.form} onSubmit={handleResetPassword}>
                            <Typography className={classes.label}>Reset Code</Typography>
                            <input
                                className={classes.input}
                                type="text"
                                placeholder="Enter your 6-digit code"
                                value={resetCode}
                                onChange={(e) => setResetCode(e.target.value)}
                                disabled={loading}
                                required
                            />

                            <Typography className={classes.label}>New Password</Typography>
                            <PasswordWithValidation
                                value={newPassword}
                                onChange={setNewPassword}
                                placeholder="Create a new password"
                                name="new_password"
                                disabled={loading}
                                inputClassName={classes.input}
                            />
                            {errors.password && (
                                <Typography style={{ color: '#d32f2f', fontSize: '13px', marginBottom: '10px', marginTop: '-8px', textAlign: 'left', width: '100%', fontWeight: '400' }}>
                                    {errors.password}
                                </Typography>
                            )}

                            <Typography className={classes.label}>Confirm Password</Typography>
                            <Box sx={{ position: 'relative', width: '100%' }}>
                                <input
                                    className={classes.input}
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    placeholder="Repeat new password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    disabled={loading}
                                    required
                                    style={{ paddingRight: '42px' }}
                                />
                                <IconButton
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                                    disabled={loading}
                                    sx={{
                                        position: 'absolute',
                                        right: 8,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        borderRadius: '8px',
                                        p: 0.35,
                                        color: 'var(--brand-text-muted, #4d4d4d)',
                                    }}
                                >
                                    {showConfirmPassword ? <VisibilityOff style={{ fontSize: 16 }} /> : <Visibility style={{ fontSize: 16 }} />}
                                </IconButton>
                            </Box>
                            {errors.confirmPassword && (
                                <Typography style={{ color: '#d32f2f', fontSize: '13px', marginBottom: '10px', marginTop: '-8px', textAlign: 'left', width: '100%', fontWeight: '400' }}>
                                    {errors.confirmPassword}
                                </Typography>
                            )}

                            <Button
                                className={classes.continueButton}
                                type="submit"
                                disabled={loading || !resetCode.trim() || !newPassword.trim()}
                                sx={{ mt: 3 }}
                            >
                                {loading ? 'Updating...' : 'Update Password'}
                            </Button>
                        </form>
                    </Box>
                )}

                <Typography className={classes.signupText} style={{ marginTop: '40px', marginBottom: '20px' }}>
                    Remember your password?{' '}
                    <span 
                        className={classes.signupLink} 
                        onClick={() => history.push('/login')}
                    >
                        Log in
                    </span>
                </Typography>
            </Box>
            <Box className={classes.footerContainer}>
                <Typography className={classes.footerText}>
                    <span className={classes.footerLink}>Terms of Service</span> and <span className={classes.footerLink}>Privacy Policy</span>.
                </Typography>
            </Box>
            <LoaderWithBackDrop loading={loading} />
        </Box>
    );
};

export default DQProV2ResetPassword;
